import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountBalance as BankIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

function MaventaActivationDialog({ open, onClose, onSuccess, t, i18n }) {
  const { user, currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [email, setEmail] = useState("");
  const [address1, setAddress1] = useState("");
  const [postCode, setPostCode] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [requestingLinking, setRequestingLinking] = useState(false);
  const [linkingRequested, setLinkingRequested] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Fetch organization details on open
  useEffect(() => {
    if (open && organizationId) {
      setLoadingDetails(true);
      setError(null);
      // Set email to current user's email
      setEmail(user?.email || "");

      // Fetch organization details for display
      axios
        .get(`/api/organizations/${organizationId}/`)
        .then((response) => {
          setOrgDetails(response.data);
          if (response.data.maventa_linking_requested_at) {
            setLinkingRequested(true);
          }
          // Use structured address fields if available
          if (response.data.address_street) {
            setAddress1(response.data.address_street);
          }
          if (response.data.address_postal_code) {
            setPostCode(response.data.address_postal_code);
          }
          if (response.data.address_city) {
            setCity(response.data.address_city);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch organization details:", err);
          setError(t("Failed to load organization details"));
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  }, [open, organizationId, user?.email, t]);

  const handleActivate = async () => {
    if (!email) {
      setError(t("Email is required"));
      return;
    }
    if (!address1 || !postCode || !city) {
      setError(t("Address, postal code and city are required"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/maventa/activate/`,
        {
          auth_email: email,
          address1: address1,
          post_code: postCode,
          city: city,
          language: i18n?.language,
        }
      );

      if (response.data.success) {
        onSuccess(response.data);
        onClose();
      } else {
        setError(response.data.error || t("Activation failed"));
      }
    } catch (err) {
      console.error("Failed to activate Maventa:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t("Failed to activate verkkolaskutus. Please try again.");
      setError(errorMessage);
      setErrorCode(err.response?.data?.error_code || null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !requestingLinking) {
      setError(null);
      setErrorCode(null);
      setLinkingRequested(false);
      onClose();
    }
  };

  const handleRequestLinking = async () => {
    setRequestingLinking(true);
    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/maventa/request-linking/`
      );
      if (response.data.success) {
        setLinkingRequested(true);
        setError(null);
        setErrorCode(null);
      } else {
        setError(response.data.error || t("maventa.linkingRequestFailed"));
      }
    } catch (err) {
      console.error("Failed to request Maventa linking:", err);
      if (err.response?.status === 409) {
        setLinkingRequested(true);
        setError(null);
        setErrorCode(null);
      } else {
        setError(
          err.response?.data?.error ||
          t("maventa.linkingRequestFailed", "Pyynnön lähettäminen epäonnistui. Yritä uudelleen.")
        );
      }
    } finally {
      setRequestingLinking(false);
    }
  };

  // Calculate OVT address preview
  const getOvtAddress = () => {
    if (orgDetails?.business_id) {
      // OVT = 0037 + business_id without dash
      return `0037${orgDetails.business_id.replace("-", "")}`;
    }
    return "";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {t("Activate E-Invoice Service")}
      </DialogTitle>

      <DialogContent dividers>
        {loadingDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Info Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                {t("Visma Sign Authorization Required")}
              </Typography>
              <Typography variant="body2">
                {t(
                  "After clicking Activate, you will receive an email from Visma Sign. You must verify your company by signing with your Finnish bank credentials."
                )}
              </Typography>
            </Alert>

            {/* Company Details Section */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1.5 }}
            >
              {t("Company Details")}
            </Typography>

            <Box
              sx={{
                bgcolor: "grey.50",
                p: 2,
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Company Name")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {orgDetails?.name || "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Business ID")} (Y-tunnus)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontFamily: "monospace" }}
                  >
                    {orgDetails?.business_id || "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Your OVT Address")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontFamily: "monospace" }}
                  >
                    {getOvtAddress() || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Email Input */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              {t("Signing Person's Email")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "The authorization email from Visma Sign will be sent to this address."
              )}
            </Typography>

            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("Enter email address")}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 3 }} />

            {/* Company Address */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
            >
              {t("Company Address")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("Required for Maventa registration")}
            </Typography>

            <TextField
              fullWidth
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              placeholder={t("Street address")}
              label={t("Street Address")}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                value={postCode}
                onChange={(e) => setPostCode(e.target.value)}
                placeholder={t("Postal code")}
                label={t("Postal Code")}
                disabled={loading}
                sx={{ width: 140 }}
              />
              <TextField
                fullWidth
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("City")}
                label={t("City")}
                disabled={loading}
              />
            </Box>

            {/* Warning */}
            <Alert severity="warning" icon={<SecurityIcon />}>
              <Typography variant="body2">
                <strong>{t("Important")}:</strong>{" "}
                {t(
                  "You will need to sign with your Finnish bank credentials (e.g., online banking). Only authorized company representatives should complete this step."
                )}
              </Typography>
            </Alert>

            {/* Linking Request Success */}
            {linkingRequested && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t("maventa.linkingRequested", "Yhdistämispyyntö lähetetty! Otamme sinuun yhteyttä pian.")}
              </Alert>
            )}

            {/* Error Display */}
            {error && !linkingRequested && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {/* For known error codes render a UI-localized message instead of
                    the raw backend string, which is generated server-side and may
                    not match the selected UI language. */}
                {errorCode === "company_already_exists"
                  ? t(
                      "maventa.companyAlreadyExists",
                      "This business ID is already registered in Maventa. Your company already has an e-invoicing account in another system. Request account linking to Verstaani using the button below."
                    )
                  : error}
                {/* Show request linking button for company_already_exists error */}
                {errorCode === "company_already_exists" && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={requestingLinking ? <CircularProgress size={16} /> : <SupportIcon />}
                      onClick={handleRequestLinking}
                      disabled={requestingLinking}
                    >
                      {requestingLinking
                        ? t("maventa.sendingRequest", "Lähetetään...")
                        : t("maventa.requestLinking", "Pyydä tilin yhdistämistä")}
                    </Button>
                  </Box>
                )}
              </Alert>
            )}

            {/* What happens next */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.secondary", mb: 1.5 }}
              >
                {t("What happens next?")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="body2">
                    {t("You'll receive an email from Visma Sign")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="body2">
                    {t("Sign with your bank credentials to verify your company")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="body2">
                    {t("Your e-invoice service will be activated automatically")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <BankIcon
                    sx={{ fontSize: 20, color: "warning.main", mt: 0.25 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      "Bank network activation takes 1-3 business days after signing"
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={loading || loadingDetails || !email || !address1 || !postCode || !city}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? <span>{t("Activating...")}</span> : <span>{t("Activate")}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(MaventaActivationDialog);
